import { Box, Stack, Typography } from "@mui/material";
import { colours } from "../../../../StyleConstants";
import { useWizardQuery } from "../../../../api/dataWranglerApiSlice";
import { QueryBuilderTypes } from "../../types/QueryBuilderTypes";
import { useSelector } from "react-redux";
import { selectSelectedDatabase } from '../../../common/databaseSlice';
import { useUserInfoQuery } from "../../../../api/userApiSlice";

interface Props {
    queries: any[];
    selectedQuery: string,
    setSelectedQuery: any,
    selectable?: boolean,
    maxHeight?: string,
}

const findInstruments = (query: any, data: any) => {
    const instruments: string[] = []
    if (query?.builder === QueryBuilderTypes.Wizard) {
        query.query.instruments.forEach((instrument: string) => {
            const instrumentName = data?.allInstruments?.[instrument]?.name || null;
            if (instrumentName) instruments.push(instrumentName);
        })
        return instruments
    }

    Object.keys(query.query || {}).forEach((key: string) => {
        instruments.push(query.query[key].name)
    })
    return instruments
}

const findAges = (data: any) => {
    if(data?.builder === QueryBuilderTypes.Wizard){
        return data?.query?.ageRange
    }

    else if (data?.builder === QueryBuilderTypes.Advanced){
        let ageGroups: any[] = []
        Object.keys(data.query || {}).forEach((key: any) => {
            ageGroups = [...ageGroups, data.query[key].ageGroups]
        })
        return ageGroups
    }
}

const QueriesList = (props: Props) => {
    const selectedDatabase = useSelector(selectSelectedDatabase);
    const { data: userData } = useUserInfoQuery({});
    const isGuestUser = userData?.id === -1
    const {
        data
    } = useWizardQuery({ database: selectedDatabase }, { skip: isGuestUser });

    const handleSelectItem = (id: number) => {
        if (Number(props.selectedQuery) === id) {
            props.setSelectedQuery("-1");
            return;
        }
        props.setSelectedQuery(id)
    }

    return (
        <Box
            sx={{
                height: '100%',
                padding: 2,
                borderRadius: "4px",
            }}
        >
            <Typography fontSize="24px" fontWeight={600}>Queries</Typography>
            <Stack
                data-testid="queries-list"
                sx={{
                    width: '100%',
                    overflow: 'auto',
                    wordWrap: 'break-word',
                    maxHeight: props.maxHeight ? props.maxHeight : '500px',
                    gap: 2
                }}
            >
                {props?.queries?.map((query: any) => {
                    return (
                        <QueryInfo
                            key={query.id}
                            title={query.name}
                            builderType={query.builder}
                            id={query.id}
                            description={query.description}
                            visits={findAges(query) || ""}
                            constructs={findInstruments(query, data) || ""}
                            selected={query.id === props.selectedQuery}
                            onClick={handleSelectItem}
                        />
                    )
                })}
            </Stack>
        </Box>
    )
}

interface QueryInfoProps {
    selected?: boolean;
    id: number;
    title: string;
    builderType: string;
    visits: any[];
    constructs: string[];
    description: string;
    onClick: (id: number) => void;
}

const QueryInfo = ({ title, builderType, selected, id, onClick, visits, constructs }: QueryInfoProps) => {
    const builder = builderType === QueryBuilderTypes.Wizard ? "Query Wizard" : "Dictionary Query Tool";
    const selectedBorder = selected ? `2px solid ${colours.orange.orange}` : "none"

    const onKeyDown = (e: any) => {
        // If the key is Enter, perform a click on the element.
        if (e.keyCode === 13) {
            e.preventDefault();
            e.target.click();
        }
    }

    return (
        <Stack
            data-testid="query-info-wrapper"
            flexDirection="row"
            onClick={() => onClick(id)}
            onKeyDown={onKeyDown}
            border={selectedBorder}
            gap={2}
            flexShrink={0}
            paddingX={4}
            paddingY={3}
            sx={{
                flexDirection: "column",
                gap: 2,
                width: "100%",
                flexShrink: 0,
                paddingX: 4,
                paddingY: 3,
                boxShadow: selectedBorder,
                backgroundColor: selected ? colours.orange.orange20 : colours.orange.orange10,
                border: selectedBorder,
                borderRadius: "8px",
                cursor: "pointer",
                borderCollapse: "separate",
            }}
        >
            <Stack flexDirection="column" gap={2} width="100%">
                <Typography fontWeight={400} fontSize="10px">
                    {builder}
                </Typography>
                <Typography fontWeight={600} fontSize="16px">
                    {title}
                </Typography>

                {builder === "Query Wizard" &&
                    <Stack flexDirection="column" width="100%" gap={1}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Typography fontWeight={600} fontSize="10px">
                                Visits:
                            </Typography>
                            <Typography fontWeight={400} fontSize="10px">
                                {visits.join(" - ")}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Typography fontWeight={600} fontSize="10px">
                                Instruments:
                            </Typography>
                            <Typography fontWeight={400} fontSize="10px">
                                {constructs.join(", ")}
                            </Typography>
                        </Box>
                    </Stack>
                }

                {builder === "Dictionary Query Tool" &&
                    visits.map((curVisit, index) => {
                        let curVisitString = ""

                        if (curVisit.length === 0) {
                            curVisitString = "(All Visits)"
                        } else {
                            curVisitString = "("
                            curVisit.forEach((ageGroup: any, index: number) => {
                                if (index === curVisit.length - 1) {
                                    curVisitString = curVisitString.concat(ageGroup.label + ")")
                                } else {
                                    curVisitString = curVisitString.concat(ageGroup.label + ", ")
                                }
                            })
                        }
                        return (
                            <Stack key={`${index}-${constructs[index]}`} flexDirection="row" gap={"5px"}>
                                <Typography fontSize="10px"> {constructs[index]} </Typography>
                                <Typography fontSize="10px"> {curVisitString}</Typography>
                            </Stack>)
                    })
                }
            </Stack>
        </Stack>
    );
}

export default QueriesList;